import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import LoginClassic from './login/LoginClassic';
import LoginMinimalCard from './login/LoginMinimalCard';
import LoginDarkGlass from './login/LoginDarkGlass';

// Maps the admin-picked auth_theme to the actual login variant. Each
// variant file is self-contained (own useLoginForm call, own layout),
// so this wrapper only owns the gates that must run before any design
// renders: logged-in redirect, site-config loading spinner, and the
// "login disabled" screen when an admin has switched login off.
const VARIANTS = {
  classic: LoginClassic,
  modern:  LoginMinimalCard,
  bold:    LoginDarkGlass,
};

const LoginPage = () => {
  const { t } = useTranslation('auth');
  const user = useSelector((state) => state.auth.user);
  const { siteConfig, loaded: configLoaded } = useSiteConfig();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  if (user) {
    return <Navigate to={returnTo || '/home'} replace />;
  }

  if (!configLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0a0a0a]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (siteConfig.login_enabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0a0a0a] px-4">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('login.disabled', 'Login Disabled')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('login.disabledDesc', 'Login is currently disabled by the site administrator. Please check back later.')}</p>
        </div>
      </div>
    );
  }

  const Variant = VARIANTS[siteConfig.auth_theme] || LoginClassic;
  return <Variant />;
};

export default LoginPage;
